import { createContext, useContext, useRef, useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { loadNamespacesWithFallback, ensureNamespaces } from '@i18n/loader'
import { INITIAL_NAMESPACES, FALLBACK_LANGUAGE, getNamespaceFromKey } from '@i18n/namespaces'
import LanguageService from '@services/LanguageService'
import { SUPPORTED_LANGUAGES } from '@constants/languages'
import styles from '@styles/LanguageLoading.module.css'

const LanguageContext = createContext(null)

/**
 * Resolve a dot-notation key from an object
 *
 * @param {string} key - Dot-notation key (e.g., 'memorama.start.title')
 * @param {Object} obj - Object to resolve from
 * @returns {*} Resolved value or undefined
 */
function resolveKey (key, obj) {
  return key.split('.').reduce(
    (acc, part) => (acc != null && acc[part] !== undefined) ? acc[part] : undefined,
    obj
  )
}

/**
 * Create translator function for programmatic translation
 *
 * @param {Object} langObj - Current language translations (merged tree)
 * @param {string} langCode - Current language code
 * @param {Object} fallbackData - ES fallback translations (merged tree, same shape as langObj)
 * @param {Function} triggerFallbackLoad - Callback to trigger namespace load
 * @returns {Function} Translator function t(key)
 */
function createTranslator (langObj, langCode, fallbackData, triggerFallbackLoad) {
  return function tFunc (key) {
    // Try current language
    const value = resolveKey(key, langObj)
    if (value !== undefined) return value

    // Try fallback (if any data loaded)
    if (fallbackData && Object.keys(fallbackData).length > 0) {
      const fallbackValue = resolveKey(key, fallbackData)
      if (fallbackValue !== undefined) {
        if (import.meta.env.MODE === 'development') {
          console.warn(`[i18n Fallback]: ${key} (${langCode} -> ${FALLBACK_LANGUAGE})`)
        }
        return fallbackValue
      }
    }

    // Key not found - trigger fallback load if applicable
    if (langCode !== FALLBACK_LANGUAGE && triggerFallbackLoad) {
      const namespace = getNamespaceFromKey(key)
      triggerFallbackLoad(namespace, langCode)
    }

    // Return placeholder for missing key
    // Development: Show key for debugging
    // Production: empty string (safer for accessible attributes than NBSP)
    if (import.meta.env.MODE === 'development') {
      console.warn(`[i18n Missing]: ${key} in (${langCode})`)
      return `[${key}]`
    }
    return ''
  }
}

/**
 * Create translation proxy for dot-notation access
 *
 * @param {Object} langObj - Current language translations (merged tree)
 * @param {string} langCode - Current language code
 * @param {Object} fallbackData - ES fallback translations (merged tree)
 * @param {Function} triggerFallbackLoad - Callback to trigger namespace load
 * @returns {Proxy} Proxy object for nested translation access
 */
function createTranslationProxy (langObj, langCode, fallbackData, triggerFallbackLoad) {
  const createNestedProxy = (path = '') => {
    return new Proxy({}, {
      get (_, prop) {
        if (typeof prop !== 'string') return undefined

        const newPath = path ? `${path}.${prop}` : prop

        // Use createTranslator for value resolution
        const translatorFunc = createTranslator(langObj, langCode, fallbackData, triggerFallbackLoad)
        const value = translatorFunc(newPath)

        // If it's a valid string (nota placeholder), return it
        // Check for both dev placeholder and production empty string
        const isPlaceholder = (typeof value === 'string' && value.startsWith('[')) || value === ''
        if (typeof value === 'string' && !isPlaceholder) {
          return value
        }

        // If it's a placeholder, check if it's an object for navigation
        const directValue = resolveKey(newPath, langObj)

        if (directValue !== undefined) {
          if (Array.isArray(directValue)) return directValue
          if (typeof directValue === 'string') return directValue
          if (typeof directValue === 'object') return createNestedProxy(newPath)
        }

        // Check fallback for object navigation
        if (fallbackData && Object.keys(fallbackData).length > 0) {
          const fallbackDirectValue = resolveKey(newPath, fallbackData)
          if (fallbackDirectValue !== undefined) {
            if (Array.isArray(fallbackDirectValue)) return fallbackDirectValue
            if (typeof fallbackDirectValue === 'string') return fallbackDirectValue
            if (typeof fallbackDirectValue === 'object') return createNestedProxy(newPath)
          }
        }

        // Return the value from createTranslator (may be placeholder)
        return value
      }
    })
  }
  return createNestedProxy()
}

/**
 * Rebuild t and tFunc from translations and fallback state
 *
 * @param {string} lang - Current language code
 * @param {Object} translations - All translations by language
 * @param {Object} fallbackData - ES fallback translations
 * @param {Function} triggerFallbackLoad - Callback to trigger namespace load
 * @returns {{ t: Proxy, tFunc: Function }}
 */

function buildTranslators (lang, translations, fallbackData, triggerFallbackLoad) {
  const currentLangData = translations[lang] || {}
  return {
    t: createTranslationProxy(currentLangData, lang, fallbackData, triggerFallbackLoad),
    tFunc: createTranslator(currentLangData, lang, fallbackData, triggerFallbackLoad)
  }
}

export function LanguageProvider ({ children }) {
  const navigate = useNavigate()
  const location = useLocation()

  // State
  const [lang, setLangState] = useState(null)
  const [translations, setTranslations] = useState({})
  const [t, setT] = useState(null)
  const [tFunc, setTFunc] = useState(null)
  const [isReady, setIsReady] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const loadingLangRef = useRef(null)
  const didFallbackToEsRef = useRef(false)
  const [fallbackTranslations, setFallbackTranslations] = useState({})
  const loadedFallbackNamespacesRef = useRef(new Set())
  const pendingFallbackNamespacesRef = useRef(new Set())
  const currentLangRef = useRef(null)

  /**
   * Tracks which language the current fallbackTranslations belong to.
   * Used by rebuild effect to avoid using stale fallback data after language change.
   */
  const fallbackForLangRef = useRef(null)

  // Determine initial language based only on URL
  useEffect(() => {
    const urlLang = LanguageService.getCurrentLanguageFromURL()
    if (urlLang) {
      setLangState(urlLang)
    } else {
      // Fallback to stored language or default
      setLangState(LanguageService.DEFAULT_LANGUAGE)
    }
  }, [location.pathname])

  // Update currentLangRef and clear fallback state on language change
  useEffect(() => {
    if (!lang) return

    // Update ref for stale response detection
    currentLangRef.current = lang

    // Clear fallback state when language changes
    // Each language gets fresh fallback loading
    setFallbackTranslations({})
    loadedFallbackNamespacesRef.current.clear()
    pendingFallbackNamespacesRef.current.clear()
  }, [lang])

  /**
   * Trigger loading of fallback namespace
   *
   * Called by translators when a key is missing in the current language.
   * Loads the ES version of the namespace and merges it into fallbackTranslations.
   *
   * Characteristics:
   * - Deduplicates: Won't load same namespace twice (checks loaded + pending)
   * - Handles stale: Ignores response if language changed during load
   * - Non-blocking: Fire-and-forget, state update triggers re-render
   *
   * @param {string} namespace - Namespace to load (e.g., 'memorama', 'core')
   * @param {string} forLang - Language that triggered the load (for stale detection)
   */
  const triggerFallbackLoad = useCallback((namespace, forLang) => {
    // Already loaded this namespace?
    if (loadedFallbackNamespacesRef.current.has(namespace)) {
      return
    }

    // Already loading this namespace?
    if (pendingFallbackNamespacesRef.current.has(namespace)) {
      return
    }

    // Mark as pending
    pendingFallbackNamespacesRef.current.add(namespace)

    if (import.meta.env.MODE === 'development') {
      console.info(`[i18n] Loading ${FALLBACK_LANGUAGE} fallback for namespace: ${namespace}`)
    }

    ensureNamespaces(FALLBACK_LANGUAGE, [namespace])
      .then(data => {
        // Ignore if language changed while loading
        if (currentLangRef.current !== forLang) {
          if (import.meta.env.MODE === 'development') {
            console.info(`[i18n] Ignoring stale fallback response for ${namespace} (lang changed from ${forLang} to ${currentLangRef.current})`)
          }
          pendingFallbackNamespacesRef.current.delete(namespace)
          return
        }

        // Track which language this fallback is for
        fallbackForLangRef.current = forLang

        // Merge into fallbackTranslations (same shape as translations[lang])
        setFallbackTranslations(prev => ({
          ...prev,
          ...data // data is already { memorama: {...} } shape from ensureNamespaces
        }))

        // Mark as loaded, remove from pending
        loadedFallbackNamespacesRef.current.add(namespace)
        pendingFallbackNamespacesRef.current.delete(namespace)

        if (import.meta.env.MODE === 'development') {
          console.info(`[i18n] Loaded ${FALLBACK_LANGUAGE} fallback for namespace: ${namespace}`)
        }
      })
      .catch(error => {
        console.error(`[i18n] Failed to load ${FALLBACK_LANGUAGE} fallback for ${namespace}:`, error)
        pendingFallbackNamespacesRef.current.delete(namespace)
      })
  }, []) // No dependencies - uses refs for stability

  // Rebuild translators when fallbackTranslations changes (Step 4)
  useEffect(() => {
    // Only rebuild if we're ready
    if (!isReady) return

    // Only rebuild if fallback actually has content
    if (Object.keys(fallbackTranslations).length === 0) return

    // Only rebuild if fallback is for the current language
    // This prevents using stale fallback data after language switch
    const currentLang = currentLangRef.current
    if (fallbackForLangRef.current !== currentLang) {
      return
    }

    if (import.meta.env.MODE === 'development') {
      console.info('[i18n] Rebuilding translators with updated fallback data')
    }

    const { t: newT, tFunc: newTFunc } = buildTranslators(
      currentLang,
      translations,
      fallbackTranslations,
      triggerFallbackLoad
    )
    setT(newT)
    setTFunc(() => newTFunc)
  }, [fallbackTranslations, translations, triggerFallbackLoad, isReady])

  // Load translations when language changes
  useEffect(() => {
    if (!lang) return

    loadingLangRef.current = lang
    setIsLoading(true)

    // Check if we already have translations for this language
    const existingData = translations[lang]
    if (existingData && Object.keys(existingData).length > 0) {
      // Already have translations, rebuild with no fallback (fresh language switch)
      const { t: newT, tFunc: newTFunc } = buildTranslators(
        lang,
        translations,
        {},
        triggerFallbackLoad
      )
      setT(newT)
      setTFunc(() => newTFunc)
      document.documentElement.lang = lang
      setIsReady(true)
      setIsLoading(false)
      return
    }

    // Load language dynamically
    loadNamespacesWithFallback(lang, INITIAL_NAMESPACES)
      .then(data => {
        // Ignore stale response
        if (loadingLangRef.current !== lang) return

        setTranslations(prev => {
          const updated = {
            ...prev,
            [lang]: { ...(prev[lang] || {}), ...data }
          }

          // Build t/tFunc from updated state
          const { t: newT, tFunc: newTFunc } = buildTranslators(
            lang,
            updated,
            {},
            triggerFallbackLoad
          )
          setT(newT)
          setTFunc(() => newTFunc)

          return updated
        })

        // Update <html lang="...">
        document.documentElement.lang = lang

        // Reser fallback flag on successful lead
        didFallbackToEsRef.current = false

        setIsReady(true)
        setIsLoading(false)
      })
      .catch(error => {
        console.error(`[i18n] Critical error loading language ${lang}:`, error)

        // ignore stale errors (user already changed language)
        if (loadingLangRef.current !== lang) return

        // Case A: Language switch failed but we have previous translations
        // Keep the UI working with the previous language
        if (t !== null && tFunc !== null) {
          setIsReady(true)
          setIsLoading(false)
          return
        }

        // Case B: First load failed, try Spanish as fallback (once)
        if (lang !== 'es' && !didFallbackToEsRef.current) {
          didFallbackToEsRef.current = true
          console.warn('[i18n] First load failed, falling back to Spanish')
          setLangState('es')
          return
        }

        // Case C: Spanish also failed or fallback already attempted
        console.error('[i18n] CRITICAL all language loading failed. App cannot start')
        setIsLoading(false)
      })
  }, [lang, triggerFallbackLoad])

  /**
   * Update translarions with additional namespaces
   * Used by useNamespaces hook to add namespaces after initial load
   *
   * @param {Object} newData - New namespace data to merge
   */
  const updateTranslations = useCallback((newData) => {
    setTranslations(prev => {
      const updated = {
        ...prev,
        [lang]: { ...(prev[lang] || {}), ...newData }
      }

      // Update t and tFunc with merged data
      const { t: newT, tFunc: newTFunc } = buildTranslators(
        lang,
        updated,
        fallbackTranslations,
        triggerFallbackLoad
      )
      setT(newT)
      setTFunc(() => newTFunc)

      return updated
    })
  }, [lang, triggerFallbackLoad, fallbackTranslations])

  /** Load additional namespaces for the current language
   * Convenience method that combines ensureNamespaces and updateTranslations
   *
   * @param {string[]} namespaces - Namespaces to load
   * @returns {Promise<Object>} Loaded translations
   */
  const loadAdditionalNamespaces = useCallback(async (namespaces) => {
    const data = await ensureNamespaces(lang, namespaces)
    updateTranslations(data)
    return data
  }, [lang, updateTranslations])

  // Function to set language and update URL
  const setLang = (newLang) => {
    if (!LanguageService.SUPPORTED_LANGUAGES.includes(newLang)) return

    LanguageService.saveLanguage(newLang)

    const currentLangFromPath = SUPPORTED_LANGUAGES.find(lang =>
      location.pathname.startsWith(`/${lang}/`) || location.pathname === `/${lang}`
    )
    const newPath = currentLangFromPath
      ? location.pathname.replace(`/${currentLangFromPath}`, `/${newLang}`)
      : `/${newLang}${location.pathname}`

    navigate(newPath, { replace: true })
  }

  // Gate ONLY on first load

  if (!isReady) {
    return (
      <div
        className={styles.languageLoading}
        role='status'
        aria-live='polite'
        aria-label='Loading language...'
      >
        <span className={styles.spinner} aria-hidden='true' />
      </div>
    )
  }

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, tFunc, isReady, isLoading, updateTranslations, loadAdditionalNamespaces }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage () {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}
